// components/support/NewTicketForm.jsx
import { useState } from "react";
import { useTranslation } from "react-i18next";

const NewTicketForm = ({ onSubmit, categories = [] }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");

  const [category, setCategory] = useState(categories?.[0]?.key || "general");
  const [priority, setPriority] = useState("normal");

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !text.trim()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("subject", subject.trim());
      formData.append("text", text.trim());
      formData.append("category", category);
      formData.append("priority", priority);

      // ✅ Attachments
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      await onSubmit(formData);

      // ✅ reset on success
      setSubject("");
      setText("");
      setPriority("normal");
      setCategory(categories?.[0]?.key || "general");
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl shadow p-4 space-y-4"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <h2 className="font-semibold text-gray-800 text-lg">
        {t("ticket_form.title", "ارسال تیکت جدید")}
      </h2>

      {/* Subject */}
      <div>
        <label className="block text-sm text-gray-700 mb-1">
          {t("ticket_form.subject", "موضوع")}
        </label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          disabled={loading}
          className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:bg-gray-100"
        />
      </div>

      {/* Category + Priority */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-700 mb-1">
            {t("ticket_form.category", "دسته‌بندی")}
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={loading}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:bg-gray-100"
          >
            {categories.length ? (
              categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))
            ) : (
              <>
                <option value="general">{t("ticket_categories.general", "عمومی")}</option>
                <option value="technical">{t("ticket_categories.technical", "فنی")}</option>
                <option value="billing">{t("ticket_categories.billing", "مالی")}</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            {t("ticket_form.priority", "اولویت")}
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={loading}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:bg-gray-100"
          >
            <option value="low">{t("ticket_priority.low", "کم")}</option>
            <option value="normal">{t("ticket_priority.normal", "عادی")}</option>
            <option value="high">{t("ticket_priority.high", "بالا")}</option>
            <option value="urgent">{t("ticket_priority.urgent", "فوری")}</option>
          </select>
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm text-gray-700 mb-1">
          {t("ticket_form.message", "متن پیام")}
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          required
          disabled={loading}
          className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:bg-gray-100"
        />
      </div>

      {/* Attachments */}
      <div>
        <label className="block text-sm text-gray-700 mb-1">
          {t("ticket_form.attachments", "فایل‌های ضمیمه")}
        </label>
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          disabled={loading}
          className="w-full text-sm"
        />

        {attachments.length > 0 && (
          <div className="mt-2 text-xs text-gray-600">
            {t("ticket_form.selected_files", "فایل‌های انتخاب‌شده")}:
            <ul className="list-disc list-inside mt-1">
              {attachments.map((f, idx) => (
                <li key={idx}>{f.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={
          loading ||
          !subject.trim() ||
          !text.trim()
        }
        className={`px-4 py-2 rounded transition text-sm text-white ${
          loading || !subject.trim() || !text.trim()
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-purple-600 hover:bg-purple-700"
        }`}
      >
        {loading
          ? t("ticket_form.submitting", "در حال ارسال...")
          : t("ticket_form.submit_button", "ثبت تیکت")}
      </button>
    </form>
  );
};

export default NewTicketForm;
